import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useBrand } from '../../../context/BrandContext';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { CarolinaFab } from '../CarolinaFab';
import { passesRoleGate } from '../../../utils/roles';
import './styles.css';

interface MenuItem {
  id: string;
  labelKey: string;
  icon: 'home' | 'template' | 'users' | 'settings' | 'building' | 'file-plus' | 'plus-circle' | 'archive' | 'edit' | 'charts' | 'bar-chart-mid' | 'cloud' | 'layers' | 'briefcase' | 'map-pin' | 'alert-triangle' | 'plug' | 'shield';
  path: string;
  requiredRoles?: string[];
  hiddenRoles?: string[];
  requiredPackageTypes?: string[];
}

// Componente para cada item del menú
function SidebarMenuItem({ 
  item, 
  isActive, 
  isMobileOpen, 
  onNavigate, 
  label 
}: { 
  item: MenuItem; 
  isActive: boolean; 
  isMobileOpen: boolean; 
  onNavigate: (path: string) => void;
  label: string;
}) {
  return (
    <li className="sidebar-item">
      <button
        className={`sidebar-link ${isActive ? 'active' : ''}`}
        onClick={() => onNavigate(item.path)}
        title={!isMobileOpen ? label : undefined}
      >
        <span className="sidebar-icon">
          <Icon name={item.icon} size={22} />
        </span>
        {isMobileOpen && <span className="sidebar-text">{label}</span>}
      </button>
    </li>
  );
}

const menuItems: MenuItem[] = [
  { id: 'home', labelKey: 'common.home', icon: 'home', path: '/reports' },
  { id: 'templates', labelKey: 'common.templates', icon: 'template', path: '/templates' },
  { id: 'my-insights', labelKey: 'common.myInsights', icon: 'bar-chart-mid', path: '/my-insights' },
  { id: 'group-reports', labelKey: 'common.groupReports', icon: 'archive', path: '/group-reports' },
  { id: 'org', labelKey: 'common.orgAdmin', icon: 'building', path: '/org', requiredRoles: ['admin', 'super'], hiddenRoles: ['super'] },
  { id: 'super', labelKey: 'super.title', icon: 'building', path: '/super', requiredRoles: ['super'] },
  { id: 'admin', labelKey: 'common.adminPanel', icon: 'shield', path: '/admin', requiredRoles: ['super'] },
];

const settingsItem: MenuItem = { id: 'settings', labelKey: 'common.configuration', icon: 'settings', path: '/settings' };

export function Sidebar({ compactAdmin = false }: { compactAdmin?: boolean }) {
  const { t } = useTranslation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const mobileBreakpoint = compactAdmin ? 1024 : 768;
  const burgerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Isologo de la marca activa sobre fondo de color primario (sidebar).
  const { brand, getLogo } = useBrand();
  const isoLogo = getLogo('iso', 'light');

  // Detectar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > mobileBreakpoint) setIsMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  useEffect(() => {
    if (!isMobileOpen) return;
    menuRef.current?.querySelector<HTMLButtonElement>('.sidebar-close')?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileOpen(false);
        burgerRef.current?.focus();
      } else if (event.key === 'Tab') {
        const items = menuRef.current?.querySelectorAll<HTMLElement>('button, [tabindex]:not([tabindex="-1"])');
        if (!items?.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileOpen]);

  const toggleMobileSidebar = () => {
    if (isMobileOpen) burgerRef.current?.focus();
    setIsMobileOpen(open => !open);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    // Cerrar el sidebar en móvil después de navegar
    if (window.innerWidth <= mobileBreakpoint) {
      setIsMobileOpen(false);
      burgerRef.current?.focus();
    }
  };

  return (
    <>
      {/* Navbar superior para móviles y tablets del panel de administración. */}
      {windowWidth <= mobileBreakpoint && (
        <div className={`sidebar-navbar${compactAdmin ? ' sidebar-navbar--admin' : ''}`}>
          <div className="sidebar-logo-icon">
            <img src={isoLogo} alt={brand.displayName} className="sidebar-slash-icon" />
          </div>
        </div>
      )}

      {/* Botón hamburguesa para móvil */}
      <button 
        ref={burgerRef}
        type="button"
        className={`sidebar-burger${compactAdmin ? ' sidebar-burger--admin' : ''}`}
        onClick={toggleMobileSidebar}
        aria-label={isMobileOpen ? t('common.close') : t('common.openMenu')}
        aria-expanded={isMobileOpen}
        aria-controls="global-sidebar"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Overlay para cerrar el sidebar en móvil */}
      {isMobileOpen && (
        <button
          type="button"
          className={`sidebar-overlay${compactAdmin ? ' sidebar-overlay--admin' : ''}`}
          aria-label={t('common.close')}
          onClick={() => {
            setIsMobileOpen(false);
            burgerRef.current?.focus();
          }}
        />
      )}

      <aside
        id="global-sidebar"
        ref={menuRef}
        inert={windowWidth <= mobileBreakpoint && !isMobileOpen}
        role={isMobileOpen ? 'dialog' : undefined}
        aria-modal={isMobileOpen ? true : undefined}
        aria-label={isMobileOpen ? brand.displayName : undefined}
        className={`sidebar sidebar-collapsed${compactAdmin ? ' sidebar--admin' : ''}${isMobileOpen ? ' sidebar-mobile-open' : ''}`}
      >
      <div className="sidebar-header">
        <button type="button" className="sidebar-brand" onClick={() => handleNavigation('/reports')} aria-label={`${brand.displayName} — ${t('common.home')}`}>
          <span className="sidebar-logo-icon">
            <img src={isoLogo} alt="" className="sidebar-slash-icon" />
          </span>
          {isMobileOpen && <span className="sidebar-brand-name">{brand.productName}</span>}
        </button>
        {isMobileOpen && (
          <button
            type="button"
            className="sidebar-close"
            aria-label={t('common.close')}
            onClick={() => {
              setIsMobileOpen(false);
              burgerRef.current?.focus();
            }}
          >
            <Icon name="x" size={18} />
          </button>
        )}
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          {menuItems
            .filter((item) => {
              const userRole = user?.role?.toLowerCase();
              if (item.hiddenRoles && userRole && item.hiddenRoles.map(r => r.toLowerCase()).includes(userRole)) {
                return false;
              }
              if (item.requiredRoles && item.requiredRoles.length > 0) {
                if (!passesRoleGate(userRole, item.requiredRoles)) {
                  return false;
                }
              }
              if (item.requiredPackageTypes && item.requiredPackageTypes.length > 0) {
                const userPackage = user?.packageType?.toUpperCase();
                if (!userPackage || !item.requiredPackageTypes.map(p => p.toUpperCase()).includes(userPackage)) {
                  return false;
                }
              }
              return true;
            })
            .map((item) => {
              let isActive = location.pathname === item.path
                || location.pathname.startsWith(`${item.path}/`);
              
              // El botón de blank-canvas debe estar activo cuando estamos en el editor
              if (item.id === 'blank-canvas' && location.pathname.includes('/reports/edit')) {
                isActive = true;
              }
              
              return (
                <SidebarMenuItem
                  key={item.id}
                  item={item}
                  isActive={isActive}
                  isMobileOpen={isMobileOpen}
                  onNavigate={handleNavigation}
                  label={t(item.labelKey)}
                />
              );
            })}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <CarolinaFab isMobileOpen={isMobileOpen} />
        <button
          className={`sidebar-link ${location.pathname === settingsItem.path ? 'active' : ''}`}
          onClick={() => handleNavigation(settingsItem.path)}
          title={!isMobileOpen ? t(settingsItem.labelKey) : undefined}
        >
          <span className="sidebar-icon">
            <Icon name={settingsItem.icon} size={24} />
          </span>
          {isMobileOpen && <span className="sidebar-text">{t(settingsItem.labelKey)}</span>}
        </button>
      </div>
    </aside>
    </>
  );
}
